/**
 * WorkDocFormatToolbar — Inline formatting toolbar for the TipTap editor.
 *
 * Groups: Font/Size | Bold/Italic/Underline/Strike | Color/Highlight | Align | Lists | Table | Case | Spacing
 */
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter,
  AlignRight, AlignJustify, List, ListOrdered, Table as TableIcon,
  CheckSquare, Pilcrow, RemoveFormatting, Undo2, Redo2, CaseSensitive,
} from "lucide-react";
import WorkDocFontSelector from "./WorkDocFontSelector";
import WorkDocFontSize from "./WorkDocFontSize";
import WorkDocColorPicker from "./WorkDocColorPicker";
import WorkDocListStyles from "./WorkDocListStyles";
import WorkDocTextCase from "./WorkDocTextCase";
import WorkDocLineSpacing from "./WorkDocLineSpacing";
import WorkDocTableMenu from "./WorkDocTableMenu";

export default function WorkDocFormatToolbar({ editor }) {
  if (!editor) return null;

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const ToolBtn = ({ icon: Icon, action, isActive, title }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={action}
      title={title}
      className={`h-8 w-8 p-0 ${isActive ? "bg-blue-600/30 text-blue-400" : "text-gray-400 hover:text-white hover:bg-gray-800"}`}
    >
      <Icon className="w-4 h-4" />
    </Button>
  );

  return (
    <div className="flex flex-wrap items-center gap-1 px-3 py-2 bg-gray-850 border-b border-gray-700 sticky top-0 z-10 overflow-x-auto">
      {/* Undo/Redo */}
      <ToolBtn icon={Undo2} action={() => editor.chain().focus().undo().run()} title="Undo" />
      <ToolBtn icon={Redo2} action={() => editor.chain().focus().redo().run()} title="Redo" />
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Font Family & Size */}
      <WorkDocFontSelector editor={editor} value={editor.getAttributes("textStyle").fontFamily} />
      <WorkDocFontSize editor={editor} value={editor.getAttributes("textStyle").fontSize} />
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Bold, Italic, Underline, Strike */}
      <ToolBtn icon={Bold} action={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} title="Bold (Ctrl+B)" />
      <ToolBtn icon={Italic} action={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} title="Italic (Ctrl+I)" />
      <ToolBtn icon={Underline} action={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive("underline")} title="Underline (Ctrl+U)" />
      <ToolBtn icon={Strikethrough} action={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive("strike")} title="Strikethrough" />
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Color & Highlight */}
      <WorkDocColorPicker
        value={editor.getAttributes("textStyle").color || "#000000"}
        onChange={(color) => editor.chain().focus().setColor(color).run()}
        label="A"
      />
      <WorkDocColorPicker
        value={editor.getAttributes("highlight").color || "#FFFF00"}
        onChange={(color) => editor.chain().focus().toggleHighlight({ color }).run()}
        label="Highlight"
      />
      <Button
        variant="ghost" size="sm"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        title="Clear formatting"
        className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-800"
      >
        <RemoveFormatting className="w-4 h-4" />
      </Button>
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Paragraph */}
      <ToolBtn icon={Pilcrow} action={() => editor.chain().focus().setParagraph().run()} isActive={editor.isActive("paragraph")} title="Paragraph" />
      {[1, 2, 3].map((l) => (
        <Button
          key={l}
          variant="ghost" size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: l }).run()}
          className={`h-8 text-xs font-bold px-2 ${editor.isActive("heading", { level: l }) ? "bg-blue-600/30 text-blue-400" : "text-gray-400 hover:text-white hover:bg-gray-800"}`}
          title={`Heading ${l}`}
        >
          H{l}
        </Button>
      ))}
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Alignment */}
      <ToolBtn icon={AlignLeft} action={() => editor.chain().focus().setTextAlign("left").run()} isActive={editor.isActive({ textAlign: "left" })} title="Align left" />
      <ToolBtn icon={AlignCenter} action={() => editor.chain().focus().setTextAlign("center").run()} isActive={editor.isActive({ textAlign: "center" })} title="Align center" />
      <ToolBtn icon={AlignRight} action={() => editor.chain().focus().setTextAlign("right").run()} isActive={editor.isActive({ textAlign: "right" })} title="Align right" />
      <ToolBtn icon={AlignJustify} action={() => editor.chain().focus().setTextAlign("justify").run()} isActive={editor.isActive({ textAlign: "justify" })} title="Align justify" />
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Lists */}
      <WorkDocListStyles editor={editor} />
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Text Case */}
      <WorkDocTextCase editor={editor} />
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Line Spacing */}
      <WorkDocLineSpacing editor={editor} />
      <Separator orientation="vertical" className="h-6 mx-1 bg-gray-700" />

      {/* Table */}
      <ToolBtn icon={TableIcon} action={addTable} isActive={editor.isActive("table")} title="Insert table" />
    </div>
  );
}
